import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { adornmentType } from 'components/_basic/Form/TextInput/TextInput/types';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';
import { adornmentIconMap } from 'components/BusinessToolForm/components/VolumeInputField/constants';

import { concatWithDot } from 'helpers/string';

import { CONSUMER_DETAILS_NAMES } from '../../../utils/form';

interface IConsumerDetailsProps extends IProps {
  chosenUnits: string;
  title: string;
}

export const ConsumerDetails: FC<IConsumerDetailsProps> = memo(({ name, disabled = false, chosenUnits, title }) => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const isPcsChosen = chosenUnits === 'pcs';
  const isMonetaryChosen =
    chosenUnits === 'single' || chosenUnits === 'thousand' || chosenUnits === 'million' || chosenUnits === 'billion';

  return (
    <FormSectionFilledCard>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, CONSUMER_DETAILS_NAMES.CONSUMER)}
                name={concatWithDot(name, CONSUMER_DETAILS_NAMES.CONSUMER)}
                label={title}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <NumberInputControlled
                control={control}
                id={concatWithDot(name, CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE)}
                name={concatWithDot(name, CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE)}
                label={t('marketAnalysisForm.overview.shareInPercentage')}
                adornmentType="percentage"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputControlled
                control={control}
                id={concatWithDot(
                  name,
                  isMonetaryChosen ? CONSUMER_DETAILS_NAMES.MONETARY_SHARE : CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE,
                )}
                name={concatWithDot(
                  name,
                  isMonetaryChosen ? CONSUMER_DETAILS_NAMES.MONETARY_SHARE : CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE,
                )}
                label={t(
                  isMonetaryChosen
                    ? 'marketAnalysisForm.overview.shareInMonetary'
                    : 'marketAnalysisForm.overview.shareInPhysical',
                  { units: 'physicals' },
                )}
                adornmentType={isPcsChosen ? 'pcs' : 'currency'}
                endAdornmentIcon={adornmentIconMap[chosenUnits as adornmentType]}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </FormSectionFilledCard>
  );
});
